import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2, FileText, Save, Eye, Check } from 'lucide-react';
import {
  fetchEmployeeProfile,
  fetchFormSubmission,
  fetchFormStatus,
  saveDraft,
  submitForm,
  adminSaveDraft,
  adminSubmitForm,
  adminFetchFormSubmission,
  adminFetchEmployeeProfile
} from '../../../services/employeeFormsApi';

const inputClass = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
const textareaClass = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none';

export default function FamilyDetailsForm() {
  const navigate = useNavigate();
  const { formCode, employeeId } = useParams();
  const [formData, setFormData] = useState({
    employeeName: '',
    designation: '',
    dateOfBirth: '',
    dateOfAppointment: '',
    dateOfRetirement: '',
    permanentAddress: '',
    familyMembers: [
      {
        name: '',
        dateOfBirth: '',
        relationship: '',
        remarks: ''
      }
    ],
    declarationAccepted: false
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Load employee profile and existing draft on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const code = formCode || 'FAMILY';
        let submissionData = null;
        let profileData = null;

        if (employeeId) {
            try {
                const submission = await adminFetchFormSubmission(employeeId, code);
                if (submission && submission.form_data) {
                    submissionData = submission.form_data;
                }
            } catch (e) {
                console.log('No admin draft found');
            }

            try {
                profileData = await adminFetchEmployeeProfile(employeeId);
            } catch (e) {
                console.log('Failed to fetch profile in admin mode');
            }
        } else {
            try {
              const status = await fetchFormStatus(code);
              if (status.status === 'SUBMITTED' || status.status === 'APPROVED') {
                navigate(`/employee/forms/${code}/preview`);
                return;
              }
            } catch (error) {
              console.log('No form status found');
            }

            try {
              const submission = await fetchFormSubmission(code);
              if (submission && submission.form_data) {
                submissionData = submission.form_data;
              }
            } catch (error) {
              console.log('No existing draft found');
            }

            try {
              profileData = await fetchEmployeeProfile();
            } catch (error) {
              console.log('Failed to fetch profile');
            }
        }

        setFormData(prev => {
            let newData = { ...prev };
            if (submissionData) {
                newData = { ...newData, ...submissionData };
            }
            if (profileData) {
                newData.employeeName = profileData.full_name || '';
                newData.designation = profileData.designation || '';
                newData.dateOfBirth = profileData.date_of_birth ? profileData.date_of_birth.split('T')[0] : '';
                newData.dateOfAppointment = profileData.date_of_joining ? profileData.date_of_joining.split('T')[0] : '';
                newData.dateOfRetirement = profileData.date_of_retirement ? profileData.date_of_retirement.split('T')[0] : '';
                newData.permanentAddress = profileData.permanent_address || profileData.current_address || '';
            }
            return newData;
        });

      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [formCode, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFamilyMemberChange = (index, field, value) => {
    const updatedMembers = [...formData.familyMembers];
    updatedMembers[index][field] = value;
    setFormData(prev => ({
      ...prev,
      familyMembers: updatedMembers
    }));
    // Clear error for this field
    const errorKey = `member_${index}_${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: ''
      }));
    }
  };

  const addFamilyMember = () => {
    if (formData.familyMembers.length < 10) {
      setFormData(prev => ({
        ...prev,
        familyMembers: [
          ...prev.familyMembers,
          {
            name: '',
            dateOfBirth: '',
            relationship: '',
            remarks: ''
          }
        ]
      }));
    }
  };

  const removeFamilyMember = (index) => {
    if (formData.familyMembers.length > 1) {
      const updatedMembers = formData.familyMembers.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        familyMembers: updatedMembers
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Employee Details - No validation needed as these are auto-populated from backend
    // employeeName, designation, dateOfBirth, dateOfAppointment, permanentAddress are read-only

    // Family Member Details - require at least one member
    if (formData.familyMembers.length === 0) {
      newErrors.familyMembers = 'At least one family member is required';
    }

    formData.familyMembers.forEach((member, index) => {
      // Only validate first member or members with data
      if (index === 0 || member.name.trim() || member.dateOfBirth || member.relationship) {
        if (!member.name.trim()) {
          newErrors[`member_${index}_name`] = 'Family Member Name is required';
        }
        if (!member.dateOfBirth) {
          newErrors[`member_${index}_dateOfBirth`] = 'Date of Birth is required';
        }
        if (!member.relationship.trim()) {
          newErrors[`member_${index}_relationship`] = 'Relationship is required';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    setSaveSuccess(false);
    try {
      const code = formCode || 'FAMILY';
      if (employeeId) {
          await adminSaveDraft(employeeId, code, formData);
      } else {
          await saveDraft(code, formData);
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Failed to save draft: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (validateForm()) {
      // Auto-save draft before preview
      await handleSaveDraft();
      const code = formCode || 'FAMILY';
      navigate(`/employee/forms/${code}/preview`, { state: formData });
    }
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      if (!formData.declarationAccepted) {
        setErrors(prev => ({
          ...prev,
          declaration: 'Please accept the declaration to submit'
        }));
        return;
      }

      try {
        const code = formCode || 'FAMILY';
        if (employeeId) {
            await adminSubmitForm(employeeId, code, formData);
            navigate(`/employees/${employeeId}/profile?tab=documents`, { replace: true });
        } else {
            await submitForm(code, formData);
            navigate(`/user-dashboard?tab=my-forms`, { replace: true });
        }
      } catch (error) {
          alert('Failed to submit form: ' + error.message);
      }
    }
  };

  const renderFamilyMemberRow = (member, index) => (
    <tr key={index} className="border-b border-gray-200">
      <td className="px-3 py-2 text-center text-sm text-gray-600">
        {index + 1}
      </td>
      <td className="px-3 py-2">
        <input
          type="text"
          value={member.name}
          onChange={(e) => handleFamilyMemberChange(index, 'name', e.target.value)}
          placeholder="Family Member Name"
          className={`${inputClass} ${errors[`member_${index}_name`] ? 'border-red-300 bg-red-50' : ''}`}
        />
        {errors[`member_${index}_name`] && (
          <p className="text-red-600 text-xs mt-1">{errors[`member_${index}_name`]}</p>
        )}
      </td>
      <td className="px-3 py-2">
        <input
          type="date"
          value={member.dateOfBirth}
          onChange={(e) => handleFamilyMemberChange(index, 'dateOfBirth', e.target.value)}
          className={`${inputClass} ${errors[`member_${index}_dateOfBirth`] ? 'border-red-300 bg-red-50' : ''}`}
        />
        {errors[`member_${index}_dateOfBirth`] && (
          <p className="text-red-600 text-xs mt-1">{errors[`member_${index}_dateOfBirth`]}</p>
        )}
      </td>
      <td className="px-3 py-2">
        <select
          value={member.relationship}
          onChange={(e) => handleFamilyMemberChange(index, 'relationship', e.target.value)}
          className={`${inputClass} ${errors[`member_${index}_relationship`] ? 'border-red-300 bg-red-50' : ''}`}
        >
          <option value="">Select Relationship</option>
          <option value="Father">Father</option>
          <option value="Mother">Mother</option>
          <option value="Spouse">Spouse</option>
          <option value="Son">Son</option>
          <option value="Daughter">Daughter</option>
          <option value="Brother">Brother</option>
          <option value="Sister">Sister</option>
          <option value="Guardian">Guardian</option>
          <option value="Friend">Friend</option>
          <option value="Relative">Relative</option>
          <option value="Other">Other</option>
        </select>
        {errors[`member_${index}_relationship`] && (
          <p className="text-red-600 text-xs mt-1">{errors[`member_${index}_relationship`]}</p>
        )}
      </td>
      <td className="px-3 py-2">
        <input
          type="text"
          value={member.remarks}
          onChange={(e) => handleFamilyMemberChange(index, 'remarks', e.target.value)}
          placeholder="Remarks"
          className={inputClass}
        />
      </td>
      <td className="px-3 py-2">
        {formData.familyMembers.length > 1 && (
          <button
            type="button"
            onClick={() => removeFamilyMember(index)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Remove Family Member"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </td>
    </tr>
  );

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading form...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-12 w-12 rounded-lg bg-blue-50 flex items-center justify-center">
            <FileText className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Family Details Form</h1>
            <p className="text-gray-600">परिवार का विवरण - Provide details of your family members.</p>
          </div>
        </div>
      </div>

      {/* Section 1: Employee Details */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Employee Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.employeeName}
              onChange={(e) => handleInputChange('employeeName', e.target.value)}
              placeholder="Enter employee name"
              className={`${inputClass} ${errors.employeeName ? 'border-red-300 bg-red-50' : ''} bg-gray-50`}
              readOnly
            />
            {errors.employeeName && (
              <p className="text-red-600 text-sm mt-1">{errors.employeeName}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Designation <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.designation}
              onChange={(e) => handleInputChange('designation', e.target.value)}
              placeholder="Enter designation"
              className={`${inputClass} ${errors.designation ? 'border-red-300 bg-red-50' : ''} bg-gray-50`}
              readOnly
            />
            {errors.designation && (
              <p className="text-red-600 text-sm mt-1">{errors.designation}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date of Birth <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.dateOfBirth}
              onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
              className={`${inputClass} ${errors.dateOfBirth ? 'border-red-300 bg-red-50' : ''} bg-gray-50`}
              readOnly
            />
            {errors.dateOfBirth && (
              <p className="text-red-600 text-sm mt-1">{errors.dateOfBirth}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date of Appointment <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.dateOfAppointment}
              onChange={(e) => handleInputChange('dateOfAppointment', e.target.value)}
              className={`${inputClass} ${errors.dateOfAppointment ? 'border-red-300 bg-red-50' : ''} bg-gray-50`}
              readOnly
            />
            {errors.dateOfAppointment && (
              <p className="text-red-600 text-sm mt-1">{errors.dateOfAppointment}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date of Retirement
            </label>
            <input
              type="date"
              value={formData.dateOfRetirement}
              onChange={(e) => handleInputChange('dateOfRetirement', e.target.value)}
              className={`${inputClass} bg-gray-50`}
              readOnly
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Permanent Address <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.permanentAddress}
              onChange={(e) => handleInputChange('permanentAddress', e.target.value)}
              placeholder="Enter permanent address"
              rows={3}
              className={`${textareaClass} ${errors.permanentAddress ? 'border-red-300 bg-red-50' : ''} bg-gray-50`}
              readOnly
            />
            {errors.permanentAddress && (
              <p className="text-red-600 text-sm mt-1">{errors.permanentAddress}</p>
            )}
          </div>
        </div>
      </div>

      {/* Section 2: Family Members Details */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Family Members Details</h2>
          <button
            type="button"
            onClick={addFamilyMember}
            disabled={formData.familyMembers.length >= 10}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            Add Family Member ({formData.familyMembers.length}/10)
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider w-16">
                  Sr. No.
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Name <span className="text-red-500">*</span>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Date of Birth <span className="text-red-500">*</span>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Relationship <span className="text-red-500">*</span>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Remarks
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-16">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {formData.familyMembers.map((member, index) => renderFamilyMemberRow(member, index))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 3: Declaration */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Declaration</h2>
        <div className="bg-gray-50 p-4 rounded-lg mb-4">
          <p className="text-sm text-gray-700 leading-relaxed">
            मैं द्वारा उपरोक्त विवरण में किसीभी प्रकार के जोड - बदल को मुख्य कार्यकारी अधिकारी के ध्यान में लाते हुए इस विवरण को अद्यावल रखने का वचन देता है।
          </p>
          <p className="text-sm text-gray-600 leading-relaxed mt-2">
            I hereby declare that I will bring any additions or changes in the above details to the attention of the Chief Executive Officer and undertake to keep this information updated.
          </p>
        </div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.declarationAccepted}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, declarationAccepted: e.target.checked }));
              if (errors.declaration) {
                setErrors(prev => ({ ...prev, declaration: '' }));
              }
            }}
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">
            I hereby declare that the above information is true and correct.
          </span>
        </label>
        {errors.declaration && (
          <p className="text-red-600 text-sm mt-2">{errors.declaration}</p>
        )}
      </div>

      {/* Section 4: Actions */}
      <div className="flex items-center justify-end gap-3 mt-8 border-t border-gray-200 pt-6">
        <button
          type="button"
          onClick={() => employeeId ? navigate(`/employees/${employeeId}/profile?tab=documents`) : navigate(-1)}
          className="inline-flex items-center gap-2 px-6 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSaveDraft}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Draft'}
        </button>
        {saveSuccess && (
          <span className="text-green-600 text-sm">Draft saved!</span>
        )}
        {!employeeId && (
          <button
            type="button"
            onClick={handlePreview}
            className="inline-flex items-center gap-2 px-6 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
        )}
        {employeeId && (
            <button
              type="button"
              onClick={handleSubmit}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              <Check className="w-4 h-4" />
              Save / Submit
            </button>
        )}
      </div>
    </div>
  );
}
